/**
 * "Quickstart" — Getting Started section (ADR-0074).
 *
 * ## The order changed, and the order is the point
 *
 * This page used to run set up → `npx syncanix init` → review → SDK → embed.
 * That put a repository scan and an optional backend SDK in front of the reader
 * before anything had appeared on their site, which is both a harder ask than
 * the product needs and not what most customers do: the widget reads the
 * product on its own, and a guided walk gets a first pass done in about an hour
 * without anyone opening an editor.
 *
 * It now runs set up → embed → let it read → review → live, with the code scan
 * and the SDK as clearly-marked optional depth AFTER the reader has something
 * working. Neither was removed; both moved behind the thing that has to happen
 * first.
 *
 * Structure + `docs.*` keys only; prose lives in the per-locale catalogs.
 * Verified against the real SDK API (`packages/sdk-node`: init /
 * createToolRegistry), the CLI, and the widget embed contract. Code blocks are
 * verbatim (locale-independent).
 */
import type { DocPageContent } from '../types';

const INIT_CODE = `import { init } from '@syncanix/sdk-node';

// Resolves your API key from SYNCANIX_API_KEY, ~/.syncanix/credentials,
// or an inline { apiKey } — in that order.
const { environment, bootDurationMs } = await init({ environment: 'development' });
console.log(\`Syncanix booted in \${bootDurationMs.toFixed(1)}ms\`);`;

const TOOL_CODE = `import { createToolRegistry } from '@syncanix/sdk-node';

const registry = createToolRegistry();

registry.tool({
  name: 'refund-order',
  description: 'Refund a customer order. Requires an admin role.',
  handler: async ({ orderId, amountCents }, ctx) => {
    // ctx.userId is the verified end user from your identity provider.
    return payments.refund({ orderId, amountCents, actor: ctx.userId });
  },
});`;

const WIDGET_CODE = `<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_live_..."
></script>`;

export const QUICKSTART_PAGE: DocPageContent = {
  slug: 'quickstart',
  titleKey: 'docs.quickstart.title',
  descriptionKey: 'docs.quickstart.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.quickstart.lead' },

    { kind: 'heading', level: 2, id: 'prerequisites', textKey: 'docs.quickstart.prereq.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.quickstart.prereq.item.account',
        'docs.quickstart.prereq.item.node',
        'docs.quickstart.prereq.item.api',
        'docs.quickstart.prereq.item.idp',
        'docs.quickstart.prereq.item.ai',
      ],
    },

    { kind: 'heading', level: 2, id: 'setup', textKey: 'docs.quickstart.setup.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.setup.body' },
    {
      kind: 'steps',
      steps: [
        {
          // Sales-led: a workspace is set up through sales (/contact), not a
          // self-serve sign-up — no payment processor is connected.
          titleKey: 'docs.quickstart.setup.workspace.title',
          textKey: 'docs.quickstart.setup.workspace.text',
        },
        {
          titleKey: 'docs.quickstart.setup.login.title',
          textKey: 'docs.quickstart.setup.login.text',
        },
        { titleKey: 'docs.quickstart.setup.pk.title', textKey: 'docs.quickstart.setup.pk.text' },
      ],
    },
    { kind: 'code', language: 'bash', code: 'npx syncanix login' },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.quickstart.setup.ai.title',
      textKey: 'docs.quickstart.setup.ai.body',
      links: {
        anthropic: {
          target: { href: 'https://console.anthropic.com', external: true },
          labelKey: 'docs.quickstart.setup.ai.link.anthropic',
        },
        openai: {
          target: { href: 'https://platform.openai.com', external: true },
          labelKey: 'docs.quickstart.setup.ai.link.openai',
        },
      },
    },

    // ── 2. EMBED. First, because it is the smallest thing that makes
    //    anything happen, and because everything after it depends on the
    //    widget being on the page.
    { kind: 'heading', level: 2, id: 'embed', textKey: 'docs.quickstart.embed.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.embed.body' },
    { kind: 'code', language: 'html', code: WIDGET_CODE },
    {
      // Z-2 on the public install narrative: the allowed-sites step belongs
      // BESIDE the snippet, which is where `/connect` puts it. Until this
      // landed, a reader could follow the published install exactly and get a
      // page where the script loads, the widget refuses to mount, and nothing
      // says why — the failure `docs.dashboard.connections.origins.stuck.body`
      // already describes, on the page nobody reads until they are stuck.
      //
      // One key, rendered from both install surfaces (here and
      // `widget-embedding.page.ts`), so the two cannot drift into saying
      // different things about the same precondition.
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.widget.origins.callout.title',
      textKey: 'docs.widget.origins.callout.body',
      links: {
        connections: {
          target: { docSlug: 'dashboard/connections' },
          labelKey: 'docs.widget.origins.callout.link',
        },
      },
    },
    { kind: 'paragraph', textKey: 'docs.quickstart.embed.note' },

    // ── 3. LET IT READ.
    { kind: 'heading', level: 2, id: 'discover', textKey: 'docs.quickstart.discover.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.discover.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.quickstart.discover.walk.title',
          textKey: 'docs.quickstart.discover.walk.text',
        },
        {
          titleKey: 'docs.quickstart.discover.ambient.title',
          textKey: 'docs.quickstart.discover.ambient.text',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.quickstart.discover.more.title',
      textKey: 'docs.quickstart.discover.more.body',
      links: {
        discovery: { target: { docSlug: 'discovery' }, labelKey: 'docs.quickstart.link.discovery' },
      },
    },

    // ── 4. REVIEW.
    { kind: 'heading', level: 2, id: 'review', textKey: 'docs.quickstart.review.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.review.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.quickstart.review.item.internal',
        'docs.quickstart.review.item.risk',
        'docs.quickstart.review.item.stepup',
      ],
    },

    // ── 5. THE DEEPER SCAN — optional, and marked as such. It is more
    //    precise than reading a screen and it costs repository access and an
    //    engineer's afternoon, so it belongs after the reader has seen the
    //    cheaper path work rather than in front of it.
    { kind: 'heading', level: 2, id: 'code-scan', textKey: 'docs.quickstart.codeScan.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.codeScan.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix init' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.quickstart.codeScan.safety.title',
      textKey: 'docs.quickstart.codeScan.safety.body',
      links: {
        cli: { target: { docSlug: 'cli/init' }, labelKey: 'docs.quickstart.link.cliInit' },
      },
    },

    // ── 6. THE SDK — the second optional depth.
    { kind: 'heading', level: 2, id: 'integrate', textKey: 'docs.quickstart.integrate.heading' },
    {
      // The backend SDK is OPTIONAL, not required (operator correction
      // 2026-07-03). Discovery + custom endpoints + custom MCP already let the
      // agent chat AND run actions under each end user's own identity; the SDK
      // adds runtime discovery/drift, SDK-native (non-HTTP) tools, and —
      // recommended for sensitive writes — intent verification. Framing kept in
      // sync with `docs.sdk.overview.when.body`.
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.quickstart.integrate.optional.title',
      textKey: 'docs.quickstart.integrate.optional.body',
    },
    { kind: 'paragraph', textKey: 'docs.quickstart.integrate.body' },
    { kind: 'code', language: 'bash', code: 'npm install @syncanix/sdk-node' },
    { kind: 'code', language: 'ts', code: INIT_CODE },
    { kind: 'paragraph', textKey: 'docs.quickstart.integrate.tools' },
    { kind: 'code', language: 'ts', code: TOOL_CODE },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.quickstart.integrate.note.title',
      textKey: 'docs.quickstart.integrate.note.body',
    },

    { kind: 'heading', level: 2, id: 'mcp', textKey: 'docs.quickstart.mcp.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.mcp.body' },

    { kind: 'heading', level: 2, id: 'promote', textKey: 'docs.quickstart.promote.heading' },
    { kind: 'paragraph', textKey: 'docs.quickstart.promote.body' },

    { kind: 'heading', level: 2, id: 'recap', textKey: 'docs.quickstart.recap.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.quickstart.recap.item.widget',
        'docs.quickstart.recap.item.mcp',
        'docs.quickstart.recap.item.audit',
        'docs.quickstart.recap.item.eu',
      ],
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.quickstart.next.architecture.title',
          textKey: 'docs.quickstart.next.architecture.text',
          to: { docSlug: 'architecture' },
        },
        {
          titleKey: 'docs.quickstart.next.glossary.title',
          textKey: 'docs.quickstart.next.glossary.text',
          to: { docSlug: 'glossary' },
        },
      ],
    },
  ],
};
